import { useEffect, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { personAddOutline, logInOutline, mailOutline } from 'ionicons/icons';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { saveJoinHints } from '../lib/localStore';

const JoinLandingPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useI18n();

  usePageView('join');

  const hints = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      emailHint: params.get('ehint') ?? undefined,
      phoneHint: params.get('phint') ?? undefined,
      ref: params.get('ref') ?? undefined,
    };
  }, [location.search]);

  useEffect(() => {
    if (hints.emailHint || hints.phoneHint || hints.ref) {
      saveJoinHints(hints);
    }
  }, [hints]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('join.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding vault-screen">
        <div className="join-hero">
          <IonIcon icon={mailOutline} className="join-icon" color="primary" />
          <h1>{t('join.headline')}</h1>
          <p>{t('join.subhead')}</p>
        </div>

        {(hints.emailHint || hints.phoneHint) && (
          <IonCard>
            <IonCardContent>
              <IonText>
                <p>{t('join.hintIntro')}</p>
              </IonText>
              {hints.emailHint && (
                <p className="join-hint">
                  <strong>{t('join.emailHint')}</strong> {hints.emailHint}
                </p>
              )}
              {hints.phoneHint && (
                <p className="join-hint">
                  <strong>{t('join.phoneHint')}</strong> {hints.phoneHint}
                </p>
              )}
              <IonText color="medium">
                <p className="join-note">{t('join.hintNote')}</p>
              </IonText>
            </IonCardContent>
          </IonCard>
        )}

        <IonCard>
          <IonCardContent>
            <IonText color="medium">
              <p>{t('join.createAccountPrompt')}</p>
            </IonText>
            <div className="join-actions">
              <IonButton expand="block" onClick={() => history.push('/sign-up')}>
                <IonIcon slot="start" icon={personAddOutline} />
                {t('home.signUp')}
              </IonButton>
              <IonButton expand="block" fill="outline" onClick={() => history.push('/sign-in')}>
                <IonIcon slot="start" icon={logInOutline} />
                {t('home.signIn')}
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>

        <style>{`
          .join-hero {
            text-align: center;
            margin-bottom: 24px;
          }
          .join-icon {
            font-size: 48px;
            margin-bottom: 12px;
          }
          .join-hero h1 {
            font-size: 24px;
            font-weight: 700;
            margin: 0 0 8px;
          }
          .join-hero p {
            color: var(--ion-color-medium);
            margin: 0;
          }
          .join-hint {
            margin: 8px 0;
            font-size: 16px;
          }
          .join-note {
            font-size: 13px;
            margin-top: 12px;
          }
          .join-actions {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 16px;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default JoinLandingPage;
